import { LinearProgress, Paper, Typography } from "@mui/material";
import classNames from "classnames";
import { Info } from "lucide-react";
import * as React from "react";

import { Tooltip } from "@/shared/foundation/primitives";

import styles from "./UsageLimit.module.css";

export interface UsageLimitProps {
  title: string;
  currentValue: string;
  /** Omit for a figure with no ceiling; the percentage row and bar are then hidden. */
  currentPercent?: number;
  limitValue?: string;
  monthly?: boolean;
  /** Stands in for the limit row when there is none — an estimate, or what the figure counts. */
  detail?: string;
  /**
   * Sizes the card to its content instead of the fixed card height, and tightens the padding,
   * the row gaps and the figure: for a row of figures above something else, rather than a page
   * of them.
   */
  compact?: boolean;
  /**
   * Shown from an info icon beside the title: how the figure is derived, or what it leaves out.
   * Pass a string where you can — the tooltip primitive emits no `aria-describedby`, so a string
   * becomes the trigger's accessible name and reaches a screen reader; a richer node cannot.
   */
  info?: React.ReactNode;
}

export const UsageLimit: React.FC<UsageLimitProps> = ({
  title,
  currentValue,
  currentPercent,
  limitValue,
  monthly,
  detail,
  compact = false,
  info,
}) => {
  const hasLimit = currentPercent !== undefined && limitValue !== undefined;
  // The tooltip reaches neither screen readers nor touch users, so a string
  // `info` is repeated as the trigger's name rather than summarised by it.
  const infoLabel = typeof info === "string" ? info : `About ${title}`;

  return (
    <Paper
      variant="outlined"
      className={classNames(styles.card, compact && styles.cardCompact)}
    >
      <div className={styles.titleRow}>
        <Typography variant="body2" className={styles.label}>
          {title}
        </Typography>
        {info !== undefined && (
          <Tooltip>
            <Tooltip.Trigger
              delay={150}
              render={
                <button
                  type="button"
                  className={styles.infoButton}
                  aria-label={infoLabel}
                />
              }
            >
              <Info size={14} aria-hidden="true" />
            </Tooltip.Trigger>
            <Tooltip.Content className={styles.infoContent}>
              {info}
            </Tooltip.Content>
          </Tooltip>
        )}
      </div>
      <Typography variant="body2" className={styles.value}>
        {currentValue}
      </Typography>
      {hasLimit ? (
        <>
          <div className={styles.limitRow}>
            <Typography variant="body2" className={styles.label}>
              <b>{Math.round(currentPercent)}%</b> of{" "}
              {monthly ? "monthly" : "total"} limit
            </Typography>
            <Typography variant="body2" className={styles.label}>
              {limitValue}
            </Typography>
          </div>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, currentPercent))}
            className={styles.progress}
            classes={{ bar: styles.progressBar }}
          />
        </>
      ) : (
        detail !== undefined && (
          <Typography
            variant="body2"
            className={classNames(styles.label, styles.detail)}
          >
            {detail}
          </Typography>
        )
      )}
    </Paper>
  );
};
